import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { ThemedText, ThemedView } from '@app/theme';
import { addCommasToNumber, getActualLimit, getPortfolioValue, getTotalLimit, roundDownToNearestHundred, trackEvent, useDarkMode } from '@app/utils';
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { filterByRepository } from '../../utils';
import CheckBox from '@app/sharedComponents/CheckBox/CheckBox';
import EditIcon from '@app/assets/svg/EditIcon';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import InputText from '@app/sharedComponents/TextInput/TextInput';
import Button from '@app/sharedComponents/Button/Button';
import { AvailableCASMap, setAvailableCAS } from '@app/store/slices/applicationSlice';
import { error } from 'console';
import useIsMobileBrowser from '@app/hooks/useIsMobileBrowser';


export type MutualFund = {
    id: string;
    name: string;
    value: string;
    availableLimit: string;
    totalLimit: string;
    isSelected?: boolean;
}


const MutualFundList = ({
    rta,
    hideUsedLimit,
    editMode = false
}: {
    rta?: string,
    hideUsedLimit?: boolean,
    editMode?: boolean
}) => {
    const { authCAS, availableCAS } = useAppSelector((state) => state.application);
    const [inputAmount, setInputAmount] = useState(0);
    const isDarkMode = useDarkMode()
    const [availableCASData, setAvailableCASData] = useState<AvailableCASMap>({});
    const [selectedFunds, setSelectedFunds] = useState<any>(null);
    const dispatch = useAppDispatch();
    const isMobileBrowser = useIsMobileBrowser();

    useEffect(() => {
        if (availableCAS) {
            setAvailableCASData(
                rta ? availableCAS && filterByRepository(availableCAS, rta === 'MFCENTRAL' ? 'ALL' : rta) : availableCAS
            );
        } else {
            setAvailableCASData(
                rta ? authCAS?.availableCAS && filterByRepository(authCAS?.availableCAS, rta === 'MFCENTRAL' ? 'ALL' : rta) : authCAS?.availableCAS
            );
        }
    }, [authCAS, availableCAS, rta]);

    const data: MutualFund[] = React.useMemo(() => {
        return availableCASData ? Object.keys(availableCASData).map((key) => {
            const value = addCommasToNumber(
                roundDownToNearestHundred(
                    getPortfolioValue(
                        [availableCASData[key]],
                        authCAS?.isinNAVMap
                    )
                )
            )
            const availableLimit = addCommasToNumber(
                roundDownToNearestHundred(
                    getTotalLimit(
                        [availableCASData[key]],
                        authCAS?.isinNAVMap,
                        authCAS?.isinLTVMap
                    )
                ))

            const totalLimit = addCommasToNumber(
                roundDownToNearestHundred(
                    getActualLimit(
                        [availableCASData[key]],
                        authCAS?.isinNAVMap,
                        authCAS?.isinLTVMap
                    )
                ))
            return {
                id: availableCASData[key]?.isinNo + '-' + availableCASData[key]?.folioNo,
                name: availableCASData[key].schemeName,
                value: `₹${value}`,
                availableLimit: `₹${availableLimit}`,
                totalLimit: `${totalLimit}`,
                ...(editMode && {
                    isSelected: availableCASData[key]?.pledgedUnits > 0
                })
            }
        }) : [];
    }, [
        availableCAS,
        authCAS,
        availableCASData
    ]);

    const handleUpdateAmount = (
        amount: number,
        selectedFunds: any
    ) => {
        const updateAvailableCASMap = { ...availableCAS };
        const key = selectedFunds.id;
        const isinNo = key.split('-')[0];
        const folioNo = key.split('-')[1];
        const item = authCAS?.availableCAS.find((item) => item.isinNo === isinNo && item.folioNo === folioNo);
        const fundCopy = { ...updateAvailableCASMap[key] }; // clone fund
        fundCopy.pledgedUnits = item && authCAS && Math.min(
            Math.ceil(
                (amount /
                    (authCAS?.isinNAVMap[fundCopy.isinNo] *
                        authCAS?.isinLTVMap[fundCopy.isinNo])) *
                Math.pow(10, 10)
            ) / Math.pow(10, 10),
            item?.totalAvailableUnits
        ).toFixed(3);
        updateAvailableCASMap[key] = fundCopy;
        dispatch(setAvailableCAS(updateAvailableCASMap));
    }

    const renderEditAmount = () => {
        if(selectedFunds) {
            trackEvent('SET_CREDIT_LIMIT_EDIT_FUND_AMOUNT_BOTTOMSHEET_PAGE_VIEWED', {
                fundName: selectedFunds?.name
            })
        }
        return authCAS && (
            <BottomSheetContainer
                isVisible={selectedFunds}
                closeOnBackdropPress={false}
                showCloseIcon={true}
                close={() => {
                    setSelectedFunds(null)
                }}
                onClose={() => {
                    setSelectedFunds(null)
                }}
                dragComponent={<ThemedView style={{ height: 12 }} />}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
                containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 ,
                    maxHeight: '80%',
                    minHeight: !isMobileBrowser ? undefined : 182, 
                }}
            >
                <ThemedView bg='bg-surface-primary' borderTopLeftRadius='lg' borderTopRightRadius='lg'>
                    <ThemedView style={{ paddingHorizontal: 16, paddingVertical: 16 }}>
                        <ThemedText variant="h5s" color="text-primary" style={{ paddingRight: 40 }}>
                            {selectedFunds?.name}
                        </ThemedText>
                        <ThemedView style={{ marginBottom: 8 }} />
                        <InputText
                            autoFocus={true}
                            keyboardType="phone-pad"
                            value={inputAmount.toString()}
                            onChangeText={(text) => {
                                if (parseInt(text) > parseInt(selectedFunds?.totalLimit?.replace(/[^0-9]/g, ""))) {
                                    return;
                                } else {
                                    const val = parseInt(text)
                                    //@ts-expect-error
                                    setInputAmount(isNaN(val) ? '' : val)
                                }
                            }}
                            showAnimation
                            showLabel
                            label={'Credit limit'}
                            isDarkMode={Boolean(isDarkMode)}
                            showRightAccessory={true}
                            rightAccessory={() => {
                                return (
                                    <ThemedView flexDirection="row" alignItems="center">
                                        <ThemedText variant='b5r' color='text-secondary'>
                                            out of ₹{selectedFunds?.totalLimit}
                                        </ThemedText>
                                    </ThemedView>
                                );
                            }}
                        />
                        {
                            inputAmount <= 100 && inputAmount >= 0 && typeof inputAmount === 'number' &&
                            <ThemedText mt='sm' variant='b4r' color='text-error'>Please enter an amount greater than Rs. 100</ThemedText>
                        }
                        <ThemedView style={{ height: 24 }} />
                        {
                            <Button
                                eventName='SET_CREDIT_LIMIT_EDIT_FUND_UPDATE_AMOUNT'
                                additionalProperties={{
                                    amount: inputAmount
                                }}
                                size='MD'
                                label={'Update amount'}
                                handlePress={() => {
                                    handleUpdateAmount(
                                        inputAmount,
                                        selectedFunds
                                    )
                                    setSelectedFunds(null)
                                }}
                                type={'SOLID'}
                                buttonLoader={false}
                                bg={""}
                                textColor={""}
                                disabled={inputAmount <= 100}
                            />
                        }
                    </ThemedView>
                    {isMobileBrowser && <ThemedView height={35} />}
                    {Platform.OS === 'android' && <ThemedView height={20} />}
                </ThemedView>
            </BottomSheetContainer>
        )
    }

    const handleTogglePortfolio = (item: any) => {
        let updateAvailableCASMap: AvailableCASMap = { ...availableCAS };
        availableCAS && Object.values(availableCAS).map((fund: any) => {
            const key = `${fund.isinNo}-${fund.folioNo}`
            if (key === item.id) {
                updateAvailableCASMap[key] = {
                    ...fund,
                    pledgedUnits: fund.pledgedUnits > 0 ? 0 : fund.totalAvailableUnits
                }
            }
        });
        dispatch(setAvailableCAS(updateAvailableCASMap))
    }

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <ThemedText
                    variant='b4m'
                    color='text-secondary'
                >
                    Mutual fund
                </ThemedText>
                <ThemedText
                    variant='b4m'
                    color='text-secondary'
                >
                    {editMode ? 'Credit limit' : 'Available credit limit'}
                </ThemedText>
            </View>

            {/* Divider */}
            <View style={styles.divider} />

            {/* List of mutual funds */}
            <ScrollView nestedScrollEnabled={true}>
                {data?.map((fund, index) => (
                    <React.Fragment key={fund.id}>
                        <View style={styles.itemContainer}>
                            <View style={{ flexDirection: 'row' , flex : 1 }}>
                                {
                                    editMode &&
                                    <View style={{ position: 'relative', top: 4, paddingRight: 12 }}>
                                        <CheckBox
                                            size={24}
                                            onToggle={() => { 
                                                handleTogglePortfolio(fund) 
                                                if(fund.isSelected){
                                                trackEvent('SET_CREDIT_LIMIT_EDITMF_MF_UNCHECKED',{
                                                    position: index + 1,
                                                        fundName: fund.name,
                                                    })
                                                }
                                            }}
                                            isChecked={fund.isSelected}
                                        />
                                    </View>
                                }
                                <View style={styles.fundInfo}>
                                    <ThemedText
                                        variant='b3m'
                                        color='text-primary'
                                    >
                                        {fund.name}
                                    </ThemedText>
                                    <ThemedText
                                        variant='b4r'
                                        color='text-secondary'
                                    >
                                        Value: {fund.value.includes('₹') ? fund.value : `₹${fund.value}`}
                                    </ThemedText>
                                </View>
                            </View>
                            <View style={styles.limitInfo}>
                                {
                                    editMode ? (
                                        <>
                                            <ThemedView style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                <ThemedView>
                                                    <ThemedText
                                                        variant='b3s'
                                                        color='text-primary'
                                                        textAlign='right'
                                                    >
                                                        {fund.availableLimit.includes('₹') ? fund.availableLimit : `₹${fund.availableLimit}`}
                                                    </ThemedText>
                                                    <ThemedText
                                                        variant='b4r'
                                                        color='text-secondary'
                                                        textAlign='right'
                                                    >
                                                        / {fund.totalLimit.includes('₹') ? fund.totalLimit : `₹${fund.totalLimit}`}
                                                    </ThemedText>
                                                </ThemedView>
                                                <TouchableOpacity hitSlop={{left : 10 , top : 10 , right : 10 , bottom : 10}} style={{ paddingLeft: 12 }} onPress={() => {
                                                    fund &&
                                                        setSelectedFunds(fund)
                                                        setInputAmount('')
                                                }}>
                                                    <EditIcon size={16} />
                                                </TouchableOpacity>
                                            </ThemedView>
                                        </>
                                    ) : hideUsedLimit ?
                                        (
                                            <>
                                                <ThemedText
                                                    variant='b3r'
                                                    color='text-primary'
                                                    textAlign='right'
                                                >
                                                    {fund.totalLimit.includes('₹') ? fund.totalLimit : `₹${fund.totalLimit}`}
                                                </ThemedText>
                                            </>
                                        ) : (
                                            <>
                                                <ThemedText
                                                    variant='b3s'
                                                    color='text-primary'
                                                    textAlign='right'
                                                >
                                                    {fund.availableLimit.includes('₹') ? fund.availableLimit : `₹${fund.availableLimit}`}
                                                </ThemedText>
                                                <ThemedText
                                                    variant='b4r'
                                                    color='text-secondary'
                                                    textAlign='right'
                                                >
                                                    / {fund.totalLimit.includes('₹') ? fund.totalLimit : `₹${fund.totalLimit}`}
                                                </ThemedText>
                                            </>
                                        )
                                }
                            </View>
                        </View>

                        {/* Add divider if not the last item */}
                        {index < data?.length - 1 && <View style={styles.itemDivider} />}
                    </React.Fragment>
                ))}
            </ScrollView>
            {editMode && renderEditAmount()}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'white',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        // paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8
    },
    headerLeft: {
        fontSize: 16,
        color: '#6B7280',
        fontWeight: '500',
    },
    headerRight: {
        fontSize: 16,
        color: '#6B7280',
        fontWeight: '500',
    },
    divider: {
        height: 1,
        backgroundColor: '#E5E7EB',
        width: '100%',
    },
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        // paddingHorizontal: 20,
        paddingVertical: 16,
    },
    fundInfo: {
        flex: 1,
        paddingRight: 8,
        maxWidth: 225
    },
    limitInfo: {
        alignItems: 'flex-end',
    },
    fundName: {
        fontSize: 16,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 4,
    },
    valueText: {
        fontSize: 16,
        color: '#6B7280',
        marginTop: 4,
    },
    limitAmount: {
        fontSize: 16,
        fontWeight: '600',
        color: '#111827',
    },
    totalLimit: {
        fontSize: 16,
        color: '#6B7280',
        marginTop: 2,
    },
    itemDivider: {
        height: 1,
        backgroundColor: '#E5E7EB',
        width: '100%',
    },
});

export default MutualFundList;